import { useEffect, useRef, useState } from 'react';
import { env } from '@/lib/env';

type TurnstileApi = {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string;
      language?: string;
      theme?: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    },
  ) => string;
  remove: (id: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * "אני לא רובוט" של Cloudflare (PRD 5.17).
 * בלי מפתח אתר מוגדר, הרכיב אומר את זה במפורש במקום להיכשל בשקט.
 */
export function Turnstile({ onToken }: { onToken: (token: string | null) => void }) {
  const holder = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!env.turnstileSiteKey || !holder.current) return;

    let widgetId: string | null = null;
    let cancelled = false;

    function render() {
      if (cancelled || !window.turnstile || !holder.current) return;
      widgetId = window.turnstile.render(holder.current, {
        sitekey: env.turnstileSiteKey,
        language: 'he',
        callback: (token) => onToken(token),
        'expired-callback': () => onToken(null),
        'error-callback': () => {
          setFailed(true);
          onToken(null);
        },
      });
    }

    if (window.turnstile) {
      render();
    } else {
      const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_URL}"]`);
      const script = existing ?? document.createElement('script');
      if (!existing) {
        script.src = SCRIPT_URL;
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', render);
      script.addEventListener('error', () => setFailed(true));
    }

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [onToken]);

  if (!env.turnstileSiteKey) {
    return (
      <p className="m-0 text-body-sm text-warn">
        אימות "אני לא רובוט" לא מוגדר עדיין, ולכן אי אפשר לשלוח את הטופס. פנו למנהל המערכת.
      </p>
    );
  }

  return (
    <div className="grid gap-1">
      <div ref={holder} />
      {failed && (
        <p className="m-0 text-body-sm text-bad">
          האימות לא נטען. נסו לרענן את הדף.
        </p>
      )}
    </div>
  );
}
